import Input from '@components/inputs/input/input.component'
import BatmanService from '@services/batman/batman.service'
import LogService from '@services/log/log.service'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const DELAY_TIME_FOR_TYPING_PAUSE = 800

export default ({
  label = '',
  disabled = false,
  readOnly = false,
  errorSpan = true,
  required = false,
  externalError = '',
  errorIcon = <i className="fa-regular fa-exclamation-triangle"></i>,
  id = '',
  name = '',
  leftLabel = '',
  rightLabel = '',
  externalIcon = null,
  value = '',
  placeholder = '',
  backgroundColor = '',
  className = '',
  onChange = null,
  onValidate = null,
  onFindAddress = null,
  requiredOnLoad = false,
  country = 'BR',
  hasTimeout = false,
}) => {
  const batmanService = useMemo(() => new BatmanService(), [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('Input Zip Code')
  const [state, setState] = useState({
    value: value,
    error: '',
    timer: null,
  })
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()

  const setMask = (zipcode) => {
    zipcode = zipcode?.length ? zipcode?.trim() : ''
    if (country === 'BR') {
      const num = zipcode?.slice(0, 9).replace(/[^\d]/g, '')
      const len = num?.length
      if (len <= 5) {
        zipcode = num?.replace(/(\d{5})/g, '$1')
      } else {
        zipcode = num?.replace(/(\d{5})(\d{1}|d{2}|d{3})/g, '$1-$2')
      }
    }
    return zipcode
  }

  const handleChange = async (event) => {
    const newState = { value: '', error: '' }
    newState.value = setMask(event)
    if (externalError) {
      newState.error = ''
    } else if (!!required && (!newState.value || newState.value.length < 1)) {
      newState.error = t('required')
    } else if (country === 'BR' && newState.value?.length !== 9) {
      newState.error = t('inputZipcode:invalidZip')
    } else {
      newState.error = ''
    }

    setState((prevState) => ({ ...prevState, value: newState.value }))
    const action = () => {
      setState((prevState) => ({ ...prevState, error: newState.error }))
      if (onValidate && typeof onValidate === 'function') {
        onValidate(!newState.error && !externalError)
      }
      if (onChange && typeof onChange === 'function') {
        onChange(newState.value?.replace('-', ''))
      }
    }

    if (hasTimeout) {
      if (state.timer) {
        clearTimeout(state.timer)
      }
      state.timer = setTimeout(() => {
        action()
      }, DELAY_TIME_FOR_TYPING_PAUSE)
      setState({ ...state })
    } else {
      action()
    }

    if (!newState.error && !loading && country === 'BR') {
      setLoading(true)
      const response = await getAddressByZipcode(newState.value?.replace('-', ''))
      if (onFindAddress && typeof onFindAddress === 'function' && response) {
        onFindAddress(response)
      }
      setLoading(false)
    }
  }

  const getAddressByZipcode = async (zipcode: string) => {
    try {
      return await batmanService.getAddressByZipcode(zipcode)
    } catch (error) {
      logService.error({ message: 'Fail to find zipcode to ' + zipcode, error })
      return { error: true }
    }
  }

  useEffect(() => {
    handleChange(value)
    //eslint-disable-next-line
  }, [value, country])

  return (
    <Input
      label={label}
      disabled={disabled}
      readOnly={readOnly}
      errorSpan={errorSpan}
      required={required}
      loading={loading}
      externalError={externalError || state?.error}
      errorIcon={errorIcon}
      id={id}
      name={name}
      leftLabel={leftLabel}
      rightLabel={rightLabel}
      requiredOnLoad={requiredOnLoad}
      externalIcon={externalIcon}
      value={state?.value}
      placeholder={placeholder || 'xxxxx-xxx'}
      backgroundColor={backgroundColor}
      className={className}
      onChange={handleChange}
    />
  )
}
